type SyncState = {
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  errorSummary?: string;
};

type CMDBSetupGuideProps = {
  sourceIdentityCount: number;
  ciCount: number;
  reviewCount: number;
  reviewRecordCount: number;
  duplicateWarningCount: number;
  writebackEnabled: boolean;
  syncAllowed: boolean;
  syncing: boolean;
  syncState?: SyncState | null;
  onStartSync: () => void;
  onReview: () => void;
};

const pluralize = (count: number, singular: string) => `${count} ${singular}${count === 1 ? '' : 's'}`;

const CMDBSetupGuide = ({
  sourceIdentityCount,
  ciCount,
  reviewCount,
  reviewRecordCount,
  duplicateWarningCount,
  writebackEnabled,
  syncAllowed,
  syncing,
  syncState,
  onStartSync,
  onReview
}: CMDBSetupGuideProps) => (
  <section className="integration-cmdb-guide" aria-labelledby="cmdb-setup-title">
    <div className="integration-cmdb-guide__intro">
      <div>
        <span className="integration-cmdb-guide__eyebrow">CMDB comparison</span>
        <h3 id="cmdb-setup-title">Compare your CMDB with REAP inventory</h3>
        <p>REAP reads a snapshot of this CMDB and matches it to discovered devices. The sync itself never changes the CMDB.</p>
      </div>
      <div className="integration-cmdb-guide__actions">
        {reviewCount > 0 && <button type="button" className="shortcut-button integration-cmdb-guide__primary" onClick={onReview}>Review {pluralize(reviewCount, 'decision')}</button>}
        <button type="button" className="shortcut-button" disabled={syncing || !syncAllowed} onClick={onStartSync} title={!syncAllowed ? 'Test and activate the connection first.' : undefined}>
          {syncing ? 'Reading CMDB…' : sourceIdentityCount ? 'Sync latest data' : 'Read CMDB data'}
        </button>
      </div>
    </div>
    {syncState && (
      <div className={`integration-cmdb-sync-state integration-cmdb-sync-state--${syncState.status}`} role="status">
        <strong>{syncState.status === 'succeeded' ? 'Sync complete' : syncState.status === 'failed' ? 'Sync needs attention' : 'REAP is reading the CMDB'}</strong>
        <span>{syncState.status === 'succeeded' ? 'Evidence and review queues have been refreshed.' : syncState.status === 'failed' ? syncState.errorSummary ?? 'Open the connection details and retry.' : 'You can close this drawer. The durable job will continue in the background.'}</span>
      </div>
    )}
    <dl className="integration-cmdb-summary">
      <div><dt>CMDB items imported</dt><dd>{sourceIdentityCount}</dd><small>{sourceIdentityCount ? `${ciCount} CIs plus relationships, services and endpoints` : 'Run the first sync to begin'}</small></div>
      <div className={reviewCount ? 'needs-attention' : ''}><dt>Match decisions required</dt><dd>{reviewCount}</dd><small>{reviewCount ? `${pluralize(reviewRecordCount, 'CMDB CI record')} affected` : 'No unresolved identity decisions'}{duplicateWarningCount ? ` · ${pluralize(duplicateWarningCount, 'duplicate warning')} separate` : ''}</small></div>
      <div><dt>Changes to your CMDB</dt><dd>{writebackEnabled ? 'Approval controlled' : 'Off'}</dd><small>{writebackEnabled ? 'Only approved operations can write' : 'REAP is report-only'}</small></div>
    </dl>
  </section>
);

export default CMDBSetupGuide;
